// AYA PRO — Feuilles d'édition de la boutique (étapes de configuration Réf 5).
import { useRef, useState } from "react";
import { X, Plus, Trash2, Pencil, Image as ImageIcon, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";

const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202f/g, " ")} FCFA`;

// Redimensionne un fichier image en dataURL (max 900px) pour stockage Mongo léger.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 900;
      const ratio = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    img.src = url;
  });
}

const Sheet = ({ title, sub, onClose, children, testid }) => (
  <>
    <div className="apd-sheet-ovl" onClick={onClose} />
    <div className="apd-sheet" data-testid={testid}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div><h3>{title}</h3>{sub && <p className="sub">{sub}</p>}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280" }} data-testid="sheet-close"><X size={20} /></button>
      </div>
      {children}
    </div>
  </>
);

export function InfosEditor({ shop, onSave, onClose }) {
  const [f, setF] = useState({ name: shop.name || "", tagline: shop.tagline || "", description: shop.description || "", city: shop.city || "", address: shop.address || "", phone_display: shop.phone_display || "", whatsapp: shop.whatsapp || "", hours: shop.hours || "", categories: (shop.categories || []).join(", "), brand_badge: shop.brand_badge || "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = () => onSave({ ...f, categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean) });
  return (
    <Sheet title="Informations de la boutique" sub="Ces informations apparaissent sur votre fiche publique." onClose={onClose} testid="editor-infos">
      {[["Nom de la boutique *", "name", "Ex : Konan Coco"], ["Slogan / accroche *", "tagline", "Ex : Des cocos frais et naturels directement de mon stand !"], ["Quartier, ville *", "city", "Ex : Attoban, Abidjan"], ["Adresse complète", "address", "Ex : Marché d'Attoban, allée 3"], ["Téléphone affiché", "phone_display", "Ex : 07 68 01 70 42"], ["WhatsApp (pour Discuter)", "whatsapp", "Ex : +2250768017042"], ["Horaires", "hours", "Ex : Tous les jours · 07h - 20h"], ["Catégories (séparées par des virgules)", "categories", "Ex : Cocos frais, Eau de coco, Packs famille"], ["Badge de marque (optionnel)", "brand_badge", "Ex : Le vrai goût du coco ivoirien !"]].map(([lbl, k, ph]) => (
        <div className="apd-input" key={k}><label>{lbl}</label><input value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`infos-${k}`} /></div>
      ))}
      <button className="apd-save" disabled={!f.name.trim() || !f.tagline.trim() || !f.city.trim()} onClick={save} data-testid="infos-save"><CheckCircle2 size={16} /> Enregistrer les informations</button>
    </Sheet>
  );
}

function PhotoField({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { onChange(await fileToDataUrl(file)); toast.success("Photo ajoutée"); } catch { toast.error("Image illisible"); }
  };
  return (
    <div className="apd-input">
      <label>{label}</label>
      {value && <img src={value} alt="" style={{ width: "100%", maxHeight: 130, objectFit: "cover", borderRadius: 13, marginBottom: 8 }} />}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="apd-save" style={{ marginTop: 0, background: "#fff", color: "#0E6A3D", border: "1.5px solid #0E8A4F" }} onClick={() => ref.current?.click()} data-testid={`${testid}-pick`}><ImageIcon size={15} /> {value ? "Changer la photo" : "Ajouter une photo"}</button>
        {value && <button className="apd-save" style={{ marginTop: 0, width: 56, background: "#FEE2E2", color: "#B91C1C" }} onClick={() => onChange(null)} data-testid={`${testid}-remove`}><Trash2 size={15} /></button>}
      </div>
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={pick} data-testid={`${testid}-file`} />
      <input style={{ marginTop: 8 }} value={value && !value.startsWith("data:") ? value : ""} onChange={(e) => onChange(e.target.value || null)} placeholder="…ou collez l'URL d'une image" data-testid={`${testid}-url`} />
    </div>
  );
}

export function PhotosEditor({ shop, onSave, onClose }) {
  const [banner, setBanner] = useState(shop.banner_image);
  const [logo, setLogo] = useState(shop.logo_image);
  return (
    <Sheet title="Photos de la boutique" sub="Une belle bannière et un logo attirent plus de clients." onClose={onClose} testid="editor-photos">
      <PhotoField label="Bannière (haut de votre fiche)" value={banner} onChange={setBanner} testid="photo-banner" />
      <PhotoField label="Logo / photo de profil" value={logo} onChange={setLogo} testid="photo-logo" />
      <button className="apd-save" disabled={!banner && !logo} onClick={() => onSave({ banner_image: banner, logo_image: logo })} data-testid="photos-save"><CheckCircle2 size={16} /> Enregistrer les photos</button>
    </Sheet>
  );
}

export function ProductsEditor({ products, onCreate, onUpdate, onDelete, onClose }) {
  const [form, setForm] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const submit = async () => {
    if (!form.name.trim() || !(Number(form.price) > 0)) { toast.error("Nom et prix (FCFA) obligatoires"); return; }
    if (form.id) await onUpdate(form.id, form); else await onCreate(form);
    setForm(null);
  };
  return (
    <Sheet title="Produits & tarifs" sub="Vos produits apparaissent sur votre boutique publique avec ajout au panier." onClose={onClose} testid="editor-products">
      {form ? (
        <>
          <div className="apd-input"><label>Nom du produit *</label><input value={form.name} onChange={set("name")} placeholder="Ex : Coco frais naturel" data-testid="product-name" /></div>
          <div className="apd-input"><label>Description courte</label><input value={form.sub} onChange={set("sub")} placeholder="Ex : Coco vert d'Attoban" data-testid="product-sub" /></div>
          <div className="apd-input"><label>Prix (FCFA) *</label><input value={form.price} onChange={set("price")} inputMode="numeric" placeholder="Ex : 500" data-testid="product-price" /></div>
          <div className="apd-input"><label>Catégorie</label><input value={form.category} onChange={set("category")} placeholder="Ex : Cocos frais" data-testid="product-category" /></div>
          <PhotoField label="Photo du produit" value={form.image} onChange={(v) => setForm((p) => ({ ...p, image: v }))} testid="product-photo" />
          <button className="apd-save" onClick={submit} data-testid="product-save"><CheckCircle2 size={16} /> {form.id ? "Mettre à jour" : "Ajouter le produit"}</button>
          <button className="apd-save" style={{ background: "#fff", color: "#6B7280", border: "1.5px solid #E5E7EB", marginTop: 8 }} onClick={() => setForm(null)} data-testid="product-cancel">Annuler</button>
        </>
      ) : (
        <>
          <div className="apd-plist">
            {products.length === 0 && <div className="apd-empty" data-testid="products-empty">Aucun produit pour le moment.<br />Ajoutez votre premier produit pour le rendre visible sur AYA.</div>}
            {products.map((p) => (
              <div className="apd-pline" key={p.id} data-testid={`pline-${p.id}`}>
                {p.image ? <img src={p.image} alt="" /> : <span style={{ width: 44, height: 44, borderRadius: 11, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>🛒</span>}
                <div className="nm"><b>{p.name}</b><span>{fcfa(p.price)} {p.in_stock ? "· En stock" : "· Rupture"}</span></div>
                <div className="act">
                  <button onClick={() => setForm({ id: p.id, name: p.name, sub: p.sub, price: String(p.price), category: p.category, image: p.image })} aria-label="Modifier" data-testid={`pline-edit-${p.id}`}><Pencil size={14} /></button>
                  <button onClick={() => onUpdate(p.id, { in_stock: !p.in_stock })} aria-label="Stock" data-testid={`pline-stock-${p.id}`} style={{ color: p.in_stock ? "#0E6A3D" : "#B91C1C" }}>{p.in_stock ? "✓" : "✕"}</button>
                  <button onClick={() => onDelete(p.id)} aria-label="Supprimer" data-testid={`pline-del-${p.id}`} style={{ color: "#B91C1C" }}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <button className="apd-save orange" onClick={() => setForm({ name: "", sub: "", price: "", category: "", image: null })} data-testid="product-add"><Plus size={16} /> Ajouter un produit</button>
        </>
      )}
    </Sheet>
  );
}

export function DeliveryEditor({ shop, onSave, onClose }) {
  const [f, setF] = useState({ zone: shop.delivery?.zone || "", fee: shop.delivery?.fee ?? "", note: shop.delivery?.note || "", pickup: shop.delivery?.pickup !== false });
  return (
    <Sheet title="Zone de livraison" sub="Indiquez les quartiers où vous livrez et vos conditions." onClose={onClose} testid="editor-delivery">
      <div className="apd-input"><label>Zone de livraison *</label><input value={f.zone} onChange={(e) => setF((p) => ({ ...p, zone: e.target.value }))} placeholder="Ex : Attoban et environs" data-testid="delivery-zone" /></div>
      <div className="apd-input"><label>Frais de livraison (FCFA)</label><input value={f.fee} onChange={(e) => setF((p) => ({ ...p, fee: e.target.value }))} inputMode="numeric" placeholder="Ex : 500" data-testid="delivery-fee" /></div>
      <div className="apd-input"><label>Note affichée aux clients</label><input value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} placeholder="Ex : Livraison à domicile à Abidjan et environs" data-testid="delivery-note" /></div>
      <label style={{ display: "flex", gap: 9, alignItems: "center", marginTop: 13, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
        <input type="checkbox" checked={f.pickup} onChange={(e) => setF((p) => ({ ...p, pickup: e.target.checked }))} style={{ width: 17, height: 17, accentColor: "#0E8A4F" }} data-testid="delivery-pickup" />
        Retrait sur place possible
      </label>
      <button className="apd-save" disabled={!f.zone.trim() && !f.pickup} onClick={() => onSave({ delivery: { zone: f.zone, fee: f.fee ? Math.round(Number(f.fee)) : null, note: f.note, pickup: f.pickup } })} data-testid="delivery-save"><CheckCircle2 size={16} /> Enregistrer la livraison</button>
    </Sheet>
  );
}

export function PublishEditor({ shop, onPublish, onPreview, onClose }) {
  const p = shop.progress || {};
  const ready = p.infos && p.photos && p.produits && p.livraison;
  const rows = [["Informations", p.infos], ["Photos", p.photos], ["Produits & tarifs", p.produits], ["Zone de livraison", p.livraison]];
  return (
    <Sheet title="Publication de la fiche" sub="Une fois publiée, votre boutique est visible par tous les clients AYA." onClose={onClose} testid="editor-publish">
      <div style={{ marginTop: 12 }}>
        {rows.map(([lbl, ok]) => (
          <div key={lbl} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13, fontWeight: 800 }}>
            <span>{lbl}</span><span style={{ color: ok ? "#0E6A3D" : "#B45309" }}>{ok ? "✓ Complété" : "À faire"}</span>
          </div>
        ))}
      </div>
      <button className="apd-save" style={{ background: "#fff", color: "#0E6A3D", border: "1.5px solid #0E8A4F" }} onClick={onPreview} data-testid="publish-preview"><Eye size={15} /> Voir l'aperçu de ma boutique</button>
      <button className="apd-save orange" disabled={!ready} onClick={onPublish} data-testid="publish-submit"><CheckCircle2 size={16} /> Publier ma fiche sur AYA</button>
      {!ready && <p className="sub" style={{ textAlign: "center", marginTop: 8 }}>Terminez les étapes restantes pour publier.</p>}
    </Sheet>
  );
}
